import { useContext } from 'react';

import { isAccountOwner } from '@hotjar/account';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { FeatureGroupEnum } from '@hotjar/webapp/domains/revenue/plan/types';
import { useCurrentUser } from '@hotjar/user';
import { useTranslation } from '@hotjar/translations';
import { capitalize } from '@hotjar/insights-common/lib/string';

import type {
  AnalyticsProduct,
  FeedbackProduct,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { PlanComparisonColumnsContext } from '@hotjar/webapp/features/Billing/PlanComparisonColumns/utils';
import { useCanSelfServeUpsell } from '@hotjar/upsell';
import { getV3SkuFromPlanId, isLegacyPlanId } from '@hotjar/product-catalog';

import { usePlanAvailability } from './usePlanAvailability';

export interface UseUpgradeButtonText {
  featureGroup: FeatureGroup;
  selectedPlan: AnalyticsProduct | FeedbackProduct;
  recommendedPlan: AnalyticsProduct | FeedbackProduct | null;
  isRecommendedPlanInCurrentFeatureGroup: boolean;
  sku?: ProductType;
  isTrafficRecommendationModal?: boolean;
}

/**
 * Determines the upgrade button's text based on some criteria.
 */
export const useUpgradeButtonText = ({
  featureGroup,
  selectedPlan,
  recommendedPlan,
  isRecommendedPlanInCurrentFeatureGroup,
  sku,
  isTrafficRecommendationModal,
}: UseUpgradeButtonText): string => {
  const { t } = useTranslation('revenue', { keyPrefix: 'plan-comparison-columns' });
  const { data: user } = useCurrentUser();
  const { site, featureGate, isUpgradeRequest } = useContext(PlanComparisonColumnsContext);
  const siteAccount = site?.account;
  const isOwner = isAccountOwner(user?.id, siteAccount);
  const hasFeatureGate = !!featureGate;
  const isAvailable = usePlanAvailability({
    featureGroup,
    selectedPlan,
    recommendedPlan,
    sku: featureGate?.sku,
  });
  const { canSelfServe } = useCanSelfServeUpsell({
    site,
    selectedSkus: isLegacyPlanId(selectedPlan.sku) ? [getV3SkuFromPlanId(selectedPlan.sku)] : [],
  });
  const currentPlanId = site?.effectivePlanId;
  const isCurrentPlan = !!currentPlanId?.includes(selectedPlan.sku);

  // Unavailable because the plan is from the current feature group
  if (!isAvailable && isCurrentPlan) {
    return isTrafficRecommendationModal ? t('traffic-current-plan') : t('current-plan');
  }

  // Unavailable, maybe because the selected plan is lower than the recommended
  if (!isAvailable) {
    // If this selectedPlan is lower than the minimumFeatureGroup on the featureGate
    // it means that it is not available on any lower plan
    if (
      hasFeatureGate &&
      featureGate?.minimumFeatureGroup &&
      FeatureGroupEnum[featureGroup] < FeatureGroupEnum[featureGate.minimumFeatureGroup]
    ) {
      return t('not-available', {
        plan: capitalize(featureGroup),
      });
    }

    // If this is a Product say 'reduced Sessions' or 'Reduced responses' depending on the product type
    if (sku === ProductType.Analytics) {
      return t('reduced-sessions');
    }

    if (sku === ProductType.Feedback) {
      return t('reduced-responses');
    }

    return t('not-available', {
      plan: capitalize(featureGroup),
    });
  }

  // Plan can't be self-served, user must go through Sales
  if (!canSelfServe) {
    return t('book-a-call');
  }

  // Non-owners cannot upgrade themselves, they can only request upgrades
  if (!isOwner) {
    return t('request-to-upgrade');
  }

  if (isUpgradeRequest) {
    // If it's an upgrade request and the recommendedPlan is selcted, let's say 'Approve upgrade'
    if (
      isTrafficRecommendationModal &&
      (recommendedPlan as AnalyticsProduct | FeedbackProduct)?.sku ===
        (selectedPlan as AnalyticsProduct | FeedbackProduct).sku
    ) {
      return t('traffic-approve-upgrade');
    }

    // If it's an upgrade request and this column's feature group, let's say 'Approve upgrade'
    if (!isTrafficRecommendationModal && isRecommendedPlanInCurrentFeatureGroup) {
      return t('approve-upgrade');
    }
  }

  // If there's a feature gate and it's the TrafficRecommendationModal, let's say 'Upgrade to Unlock'
  if (hasFeatureGate && isTrafficRecommendationModal) {
    return t('upgrade-to-unlock');
  }

  // If there's a feature gate and it's minimum is exactly this column's feature group, let's say 'Upgrade to Unlock'
  if (hasFeatureGate && isRecommendedPlanInCurrentFeatureGroup) {
    return t('upgrade-to-unlock');
  }

  // If there's a feature gate and it's price agnostic
  if (isAvailable && hasFeatureGate && featureGate.isPlanAgnostic) {
    return t('upgrade-to-unlock');
  }

  // In any other case say 'Upgrade to Plus/Business'
  return t('upgrade-to-plan', {
    plan: capitalize(featureGroup),
  });
};
